import {
  hooks,
  AddonBeam,
  Appear,
  Base,
  Flex,
  Icon,
  Input,
  InputContainer,
  InputContent,
  Text,
  Ribbon,
} from '@bitrise/bitkit';
import { TraceProfile } from '@trace/types';
import jwtDecode from 'jwt-decode';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router';

import dataAtIds from '../../dataAtIds.json';
import { trackEvent } from '../../utils/tracking';
import { FeatureContext } from '../Features/useFeatures';
import Page from '../Page/Page';
import AppSwitcherList from './AppSwitcherList';
import TraceInfoCard from './TraceInfoCard';

const tabletUp = '62.5em'; // >= 1000px

const AppSwitcher: FunctionComponent = () => {
  const matchQuery = hooks.useMediaQuery([tabletUp]);
  const location = useLocation();
  const { hasFeature } = useContext(FeatureContext);
  const params = useMemo(() => new URLSearchParams(location.search), [location]);
  const token = params.get('token');
  const [search, setSearch] = useState('');

  /**
   * Use a side-effect to:
   *  - Track visits to this page (i.e. whenever this comoponent is mounted)
   */
  useEffect(() => {
    trackEvent('AppSelector.Visit', {});
  }, []);

  /**
   * If there is a token inside the URL and they're on the AppSwitcher page
   * then they've come from the Bitrise app and we want to bump them to
   * the Trace authentication route.
   */
  if (token) {
    try {
      const data = jwtDecode(token) as { accountDetails: TraceProfile };
      return <Redirect to={`/${data.accountDetails.appId}?${params.toString()}`} />;
    } catch (e) {
      params.delete('token');
      return <Redirect to={`/?${params.toString()}`} />;
    }
  }

  return (
    <Page data-trace-at-id={dataAtIds.AppSwitcherPage}>
      <AddonBeam
        addonIcon="Trace"
        addonName="Trace"
        backgroundColor="grape-4"
        isBeta
        textColor="gray-1"
      />
      {hasFeature('enable-ongoing-maintenance-banner') && (
        <Ribbon type="inform">
          <Text align="middle">
            Access to Trace Beta will be closed on 1st October 2021. After that date, you will no
            longer be able to access the Trace dashboard.
          </Text>
        </Ribbon>
      )}
      <Base paddingHorizontal="x8" paddingVertical="x16">
        <Flex
          gap="x16"
          {...(matchQuery(tabletUp)
            ? { direction: 'horizontal', maxWidth: '83.5rem' }
            : { direction: 'vertical' })}
        >
          <Flex maxWidth={matchQuery(tabletUp) ? '25.25rem' : undefined}>
            <Appear animation="Fade">
              <TraceInfoCard />
            </Appear>
          </Flex>

          <Flex initial="none" grow>
            <Flex maxWidth={matchQuery(tabletUp) ? '40rem' : undefined}>
              <InputContainer margin="x16">
                <InputContent>
                  <Input
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    placeholder="Search apps..."
                    value={search}
                  />
                  <Icon name="Magnifier" />
                </InputContent>
              </InputContainer>

              <Text Component="h3" config="4" margin="x6">
                Your Apps
              </Text>

              <AppSwitcherList search={search} />
            </Flex>
          </Flex>
        </Flex>
      </Base>
    </Page>
  );
};

export default AppSwitcher;
